import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import HeaderNavigation from '../../../components/ui/HeaderNavigation';
import CameraViewfinder from './components/CameraViewfinder';
import LocationDisplay from './components/LocationDisplay';
import DateTimeControls from './components/DateTimeControls';
import PermissionModal from './components/PermissionModal';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import attendanceService from '../../../services/attendanceService';
import employeeService from '../../../services/employeeService';
import NotificationToast from '../../../components/ui/NotificationToast';
import { getPunchState } from '../../../utils/attendanceHelpers';

const SelfiePunchIn = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [step, setStep] = useState('permissions');
  const [permissionType, setPermissionType] = useState('camera');
  const [showPermissionModal, setShowPermissionModal] = useState(true);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [dateTime, setDateTime] = useState(new Date());
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasLocationPermission, setHasLocationPermission] = useState(false);
  const [hasCameraPermission, setHasCameraPermission] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [notification, setNotification] = useState(null);
  const [hrPermission, setHrPermission] = useState(false);

  useEffect(() => {
    loadEmployeeData();
  }, [userProfile]);

  const loadEmployeeData = async () => {
    if (!userProfile?.email) return;

    try {
      const result = await employeeService.getEmployees({ search: userProfile.email });
      if (result.success && result.data && result.data.length > 0) {
        setEmployee(result.data[0]);
        // Check if user has HR permissions
        const isHr = userProfile.role === 'hr_admin' || userProfile.role === 'super_admin';
        setHrPermission(isHr);
      }
    } catch (err) {
      console.error('Error loading employee data:', err);
    }
  };

  useEffect(() => {
    const checkExistingPunchIn = async () => {
      if (!employee?.id) return;

      try {
        const result = await attendanceService.getTodayStatus(employee.id);
        const { isPunchedIn } = getPunchState(result.data);
        if (result.success && isPunchedIn) {
          navigate('/erp/employee-self-service', { state: { activeTab: 'attendance' } });
        }
      } catch (err) {
        console.error('Error checking existing punch in:', err);
      }
    };

    if (employee) {
      checkExistingPunchIn();
    }
  }, [employee, navigate]);

  const handlePermissionAllow = () => {
    if (permissionType === 'camera') {
      setHasCameraPermission(true);
      setPermissionType('location');
    } else {
      setHasLocationPermission(true);
      setShowPermissionModal(false);
      setStep('capture');
    }
  };

  const handlePermissionDeny = () => {
    navigate('/erp/employee-self-service', { state: { activeTab: 'attendance' } });
  };

  const handlePhotoCapture = (imageUrl, blob) => {
    setCapturedPhoto({ url: imageUrl, blob });
  };

  const handleLocationCapture = (location) => {
    setLocationData(location);
  };

  const handleDateTimeChange = (newDateTime) => {
    setDateTime(newDateTime);
  };

  const handleCancel = () => {
    navigate('/erp/employee-self-service', { state: { activeTab: 'attendance' } });
  };

  const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleSubmit = async () => {
    if (!capturedPhoto || !locationData || !employee) {
      setNotification({ message: 'Please capture photo and location', type: 'error' });
      return;
    }

    setIsProcessing(true);

    try {
      // Convert blob to base64
      const photoBase64 = await convertBlobToBase64(capturedPhoto.blob);

      const punchInData = {
        employee_id: employee.id,
        photo: photoBase64,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        location: {
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          accuracy: locationData.accuracy,
          address: locationData.address
        },
        location_address: locationData.address,
        location_accuracy: locationData.accuracy,
        clock_in_time: hrPermission ? dateTime.toISOString() : undefined,
        entry_method: 'selfie',
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        }
      };

      const result = await attendanceService.clockIn(punchInData);

      if (result.success) {
        setNotification({ message: 'Punched in successfully!', type: 'success' });
        setTimeout(() => {
          navigate('/erp/employee-self-service', { state: { activeTab: 'attendance' } });
        }, 1500);
      } else {
        setNotification({ message: result.error || 'Failed to punch in', type: 'error' });
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Error punching in:', err);
      setNotification({ message: 'Failed to punch in. Please try again.', type: 'error' });
      setIsProcessing(false);
    }
  };

  if (showPermissionModal) {
    return (
      <PermissionModal
        type={permissionType}
        onAllow={handlePermissionAllow}
        onDeny={handlePermissionDeny}
        isVisible={showPermissionModal}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        user={userProfile}
        notifications={[]}
        showSidebarToggle={false}
      />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {notification && (
            <NotificationToast
              message={notification.message}
              type={notification.type}
              onDismiss={() => setNotification(null)}
              position="top-right"
              autoHideDuration={3000}
            />
          )}

          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 md:gap-4 mb-2 md:mb-3">
              <button
                onClick={handleCancel}
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-card hover:bg-muted flex items-center justify-center transition-smooth shadow-elevation-1"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} className="md:w-6 md:h-6" />
              </button>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">Punch In</h1>
            </div>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground ml-0 md:ml-16">
              Capture your selfie and location to record your attendance
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8 mb-6 md:mb-8">
            <div className="space-y-4 md:space-y-6">
              <div className="bg-card rounded-xl overflow-hidden shadow-elevation-2" style={{ height: '400px', minHeight: '400px' }}>
                <CameraViewfinder
                  onCapture={handlePhotoCapture}
                  onCancel={handleCancel}
                  isCapturing={isProcessing}
                />
              </div>

              {capturedPhoto && (
                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2">
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground">Captured Photo</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCapturedPhoto(null)}
                      iconName="X"
                    >
                      Retake
                    </Button>
                  </div>
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-muted">
                    <img
                      src={capturedPhoto.url}
                      alt="Captured selfie for attendance verification"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4 md:space-y-6">
              <LocationDisplay
                onLocationCapture={handleLocationCapture}
                isCapturing={isProcessing}
              />

              <DateTimeControls
                onDateTimeChange={handleDateTimeChange}
                hrPermission={hrPermission}
              />
            </div>
          </div>

          <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 md:gap-6">
              <div className="flex items-start gap-3 md:gap-4 w-full sm:w-auto">
                <Icon name="Info" size={20} className="md:w-6 md:h-6 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-base lg:text-lg text-foreground font-medium mb-1">Ready to punch in?</p>
                  <p className="text-xs md:text-sm text-muted-foreground">Make sure your photo and location are captured correctly</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isProcessing}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  disabled={!capturedPhoto || !locationData || isProcessing}
                  loading={isProcessing}
                  iconName="Check"
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  {isProcessing ? 'Processing...' : 'Confirm Punch In'}
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8 bg-muted/50 rounded-xl p-4 md:p-6">
            <div className="flex items-start gap-3 md:gap-4">
              <Icon name="Shield" size={18} className="md:w-5 md:h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm text-muted-foreground">
                  Your selfie and location data are encrypted and stored securely. This information is used solely for attendance verification and workplace compliance. We follow GDPR standards to protect your privacy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SelfiePunchIn;


